import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs';

import { ProfileService } from '@tfs/auth';
import { LOCALE_TO_DATE_FORMAT } from '@tfs/date-format';

import { UserOnboardingProfileDataStore } from './data-access/user-onboarding-profile.data-store';
import { TermsAcceptanceModalComponent } from './feature-terms-acceptance-modal/terms-acceptance-modal.component';
import { UserOnboardingModalComponent } from './feature-user-onboarding-modal/user-onboarding-modal.component';
import {
  OnboardingStatusDto,
  TermsAcceptanceStatusDto,
} from './models/user-onboarding-modal.models';

@Injectable({
  providedIn: 'root',
})
export class UserOnboardingService {
  private readonly profileService = inject(ProfileService);
  private readonly dialog = inject(MatDialog);
  private readonly dataStore = inject(UserOnboardingProfileDataStore);

  openOnboardingModal(): void {
    // Every user (admin or not) must accept terms before onboarding is evaluated
    if (
      this.profileService.userProfile()?.termsAcceptanceStatus === TermsAcceptanceStatusDto.Required
    ) {
      this.dialog
        .open(TermsAcceptanceModalComponent, {
          disableClose: true,
          width: '458px',
          panelClass: 'terms-acceptance-modal-panel',
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => this.openOnboardingFlow());
      return;
    }

    this.openOnboardingFlow();
  }

  private openOnboardingFlow(): void {
    const isAdmin = this.profileService.isAdmin();
    const onboardingStatus = this.profileService.userProfile()?.onboardingStatus;
    const locale = this.profileService.userProfile()?.locale;

    // Non-admin users are automatically marked as onboarded on first login
    if (!isAdmin) {
      if (onboardingStatus === OnboardingStatusDto.Invited) {
        const dateFormat = LOCALE_TO_DATE_FORMAT[locale ?? 'de-DE'];
        this.dataStore
          .updateStatusDateFormat(OnboardingStatusDto.Onboarded, dateFormat)
          .pipe(take(1))
          .subscribe();
      }
      return;
    }

    // Admin users see the onboarding modal if not yet completed
    if (onboardingStatus === OnboardingStatusDto.Onboarded) {
      return;
    }

    this.dialog.open(UserOnboardingModalComponent, {
      disableClose: true,
    });
  }
}
